/* global axios */
import ApiClient from './ApiClient';
import { resourcePath, requestConfig, withQuery } from './lais/httpHelpers';
import { IMOVEIS_ENDPOINTS } from './lais/endpoints';

const requestConfigWithIdempotency = (payload = {}) => {
  const config = requestConfig();
  const idempotencyKey = payload.idempotency_key || payload.idempotencyKey;
  if (idempotencyKey) {
    config.headers = {
      ...config.headers,
      'Idempotency-Key': idempotencyKey,
    };
  }
  return config;
};

class ImovelAPI extends ApiClient {
  constructor() {
    super('imoveis', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(withQuery(this.url, params), requestConfig());
  }

  externalProperties(params = {}) {
    return axios.get(
      withQuery(
        resourcePath(this.url, IMOVEIS_ENDPOINTS.externalProperties),
        params
      ),
      requestConfig()
    );
  }

  duplicateCandidates(params = {}) {
    return axios.get(
      withQuery(
        resourcePath(this.url, IMOVEIS_ENDPOINTS.duplicateCandidates),
        params
      ),
      requestConfig()
    );
  }

  rejectDuplicateCandidate(id, payload = {}) {
    return axios.post(
      resourcePath(
        this.url,
        IMOVEIS_ENDPOINTS.duplicateCandidates,
        encodeURIComponent(id),
        'reject'
      ),
      payload,
      requestConfig()
    );
  }

  identityMergeUrl(action) {
    return resourcePath(
      this.baseUrl(),
      'actions',
      'imovel_identity_merge',
      action
    );
  }

  planIdentityMerge(payload = {}) {
    return axios.post(this.identityMergeUrl('plan'), payload, requestConfig());
  }

  applyIdentityMerge(payload = {}) {
    return axios.post(this.identityMergeUrl('apply'), payload, requestConfig());
  }

  revertIdentityMerge(payload = {}) {
    return axios.post(
      this.identityMergeUrl('revert'),
      payload,
      requestConfig()
    );
  }

  empreendimentos(params = {}) {
    return axios.get(
      withQuery(
        resourcePath(this.url, IMOVEIS_ENDPOINTS.empreendimentos),
        params
      ),
      requestConfig()
    );
  }

  empreendimentoDetail(id) {
    return axios.get(
      resourcePath(
        this.url,
        IMOVEIS_ENDPOINTS.empreendimentos,
        encodeURIComponent(id)
      ),
      requestConfig()
    );
  }

  create(payload) {
    return axios.post(this.url, { imovel: payload }, requestConfig());
  }

  show(id) {
    return axios.get(resourcePath(this.url, id), requestConfig());
  }

  update(id, payload) {
    return axios.patch(
      resourcePath(this.url, id),
      { imovel: payload },
      requestConfig()
    );
  }

  importXml(payload) {
    return axios.post(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.importXml),
      payload,
      requestConfig()
    );
  }

  importStatus() {
    return axios.get(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.importStatus),
      requestConfig()
    );
  }

  estimatePrice(payload = {}) {
    return axios.post(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.estimatePrice),
      payload,
      requestConfig()
    );
  }

  taxFees(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.taxFees),
      payload,
      requestConfig()
    );
  }

  normalizeAddress(payload) {
    return axios.post(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.normalizeAddress),
      payload,
      requestConfig()
    );
  }

  autocompleteAddress(payload) {
    return axios.post(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.autocompleteAddress),
      payload,
      requestConfig()
    );
  }

  nearby(params = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, IMOVEIS_ENDPOINTS.nearby), params),
      requestConfig()
    );
  }

  completeAddress(id, payload) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.completeAddress),
      payload,
      requestConfig()
    );
  }

  simulateFinancing(id, payload) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.simulateFinancing),
      payload,
      requestConfig()
    );
  }

  similar(id, params = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, id, IMOVEIS_ENDPOINTS.similar), params),
      requestConfig()
    );
  }

  trackRecommendationClick(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.trackRecommendationClick),
      payload,
      requestConfig()
    );
  }

  tenant(id) {
    return axios.get(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.tenant),
      requestConfig()
    );
  }

  entryInformation(id, params = {}) {
    return axios.get(
      withQuery(
        resourcePath(this.url, id, IMOVEIS_ENDPOINTS.entryInformation),
        params
      ),
      requestConfig()
    );
  }

  entryAccesses(id) {
    return axios.get(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.entryAccesses),
      requestConfig()
    );
  }

  createEntryAccess(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.entryAccesses),
      { entry_access: payload },
      requestConfig()
    );
  }

  updateEntryAccess(id, entryAccessId, payload = {}) {
    return axios.patch(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.entryAccesses,
        entryAccessId
      ),
      { entry_access: payload },
      requestConfig()
    );
  }

  deleteEntryAccess(id, entryAccessId) {
    return axios.delete(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.entryAccesses,
        entryAccessId
      ),
      requestConfig()
    );
  }

  identity(id) {
    return axios.get(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.identity),
      requestConfig()
    );
  }

  createAlias(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.aliases),
      payload,
      requestConfig()
    );
  }

  reactivate(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.reactivate),
      payload,
      requestConfig()
    );
  }

  publish(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.publish),
      payload,
      requestConfigWithIdempotency(payload)
    );
  }

  unpublish(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.unpublish),
      payload,
      requestConfigWithIdempotency(payload)
    );
  }

  suspendListing(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.suspendListing),
      payload,
      requestConfigWithIdempotency(payload)
    );
  }

  relist(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.relist),
      payload,
      requestConfigWithIdempotency(payload)
    );
  }

  notifyAvailability(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.notifyAvailability),
      payload,
      requestConfig()
    );
  }

  updateMediaQa(id, payload = {}) {
    return axios.patch(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.mediaQa),
      payload,
      requestConfig()
    );
  }

  portalSyncs(id) {
    return axios.get(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.portalSyncs),
      requestConfig()
    );
  }

  listings(id, params = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, id, IMOVEIS_ENDPOINTS.listings), params),
      requestConfig()
    );
  }

  listing(id, listingId) {
    return axios.get(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.listings, listingId),
      requestConfig()
    );
  }

  listingVersions(id, listingId) {
    return axios.get(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.listings,
        listingId,
        IMOVEIS_ENDPOINTS.versions
      ),
      requestConfig()
    );
  }

  listingDiff(id, listingId, params = {}) {
    return axios.get(
      withQuery(
        resourcePath(
          this.url,
          id,
          IMOVEIS_ENDPOINTS.listings,
          listingId,
          IMOVEIS_ENDPOINTS.diff
        ),
        params
      ),
      requestConfig()
    );
  }

  listingLifecyclePreview(id, listingId, params = {}) {
    return axios.get(
      withQuery(
        resourcePath(
          this.url,
          id,
          IMOVEIS_ENDPOINTS.listings,
          listingId,
          IMOVEIS_ENDPOINTS.lifecyclePreview
        ),
        params
      ),
      requestConfig()
    );
  }

  syncPortal(id, portal = 'olx', payload = {}) {
    return axios.post(
      resourcePath(this.url, id, IMOVEIS_ENDPOINTS.portalSyncs, portal),
      payload,
      requestConfig()
    );
  }

  unpublishPortal(id, portal = 'olx', payload = {}) {
    return axios.post(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.portalSyncs,
        portal,
        IMOVEIS_ENDPOINTS.unpublish
      ),
      payload,
      requestConfig()
    );
  }

  ownerContactWeeklyCount(id) {
    return axios.get(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.ownerContact,
        IMOVEIS_ENDPOINTS.weeklyCount
      ),
      requestConfig()
    );
  }

  ownerContactPortfolio(id, params = {}) {
    return axios.get(
      withQuery(
        resourcePath(
          this.url,
          id,
          IMOVEIS_ENDPOINTS.ownerContact,
          IMOVEIS_ENDPOINTS.portfolio
        ),
        params
      ),
      requestConfig()
    );
  }

  revealOwnerContact(id, payload = {}, options = {}) {
    const config = requestConfig();
    if (options.hash) {
      config.headers = { ...config.headers, Hash: options.hash };
    }

    return axios.post(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.ownerContact,
        IMOVEIS_ENDPOINTS.reveal
      ),
      payload,
      config
    );
  }

  trackOwnerContactChannel(id, payload = {}) {
    return axios.post(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.ownerContact,
        IMOVEIS_ENDPOINTS.trackChannel
      ),
      { owner_contact: payload },
      requestConfig()
    );
  }

  recordOwnerCaptureAttempt(id, payload = {}, options = {}) {
    const config = requestConfig();
    if (options.idempotencyKey) {
      config.headers = {
        ...config.headers,
        'Idempotency-Key': options.idempotencyKey,
      };
    }

    return axios.post(
      resourcePath(
        this.url,
        id,
        IMOVEIS_ENDPOINTS.ownerContact,
        IMOVEIS_ENDPOINTS.recordAttempt
      ),
      { owner_contact: payload },
      config
    );
  }

  delete(id) {
    return axios.delete(resourcePath(this.url, id), requestConfig());
  }

  cancelImport(tokenOrPayload) {
    const payload =
      tokenOrPayload && typeof tokenOrPayload === 'object'
        ? tokenOrPayload
        : { token: tokenOrPayload };

    return axios.post(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.cancelImport),
      payload,
      requestConfig()
    );
  }

  programarDestaque(payload) {
    return axios.post(
      resourcePath(this.url, IMOVEIS_ENDPOINTS.programarDestaque),
      payload,
      requestConfig()
    );
  }
}

export default new ImovelAPI();
